import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TrendCard({ player, type, direction }) {
  const navigate = useNavigate();
  const isUp = direction === "up";
  const stats = player.stats || {};

  return (
    <motion.div
      initial={{ opacity: 0, x: isUp ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`bg-astros-surface border-l-4 ${isUp ? "border-l-green-500" : "border-l-red-500"} border border-astros-border p-3 cursor-pointer hover:border-astros-orange transition-colors`}
      onClick={() => navigate(`/player/${player.id}`)}
      data-testid={`trend-${direction}-${player.id}`}
    >
      <div className="flex items-center gap-3">
        <img
          src={player.photo_url}
          alt={player.name}
          className="w-10 h-10 object-cover bg-astros-navy"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=40&h=40&fit=crop"; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-ibm-plex font-semibold text-white text-sm truncate">{player.name}</h4>
              <span className="text-xs text-astros-muted">{player.position}</span>
            </div>
            <div className={`flex items-center gap-1 text-xs font-bold ${isUp ? "text-green-400" : "text-red-400"}`}>
              {isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {player.trend_diff > 0 ? "+" : ""}{player.trend_diff?.toFixed(3)}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-1.5 text-xs">
            {type === "hitter" ? (
              <>
                <div>
                  <span className="text-astros-muted">Season OPS: </span>
                  <span className="text-white font-semibold">{player.season_ops?.toFixed(3)}</span>
                </div>
                <div>
                  <span className="text-astros-muted">Last 7 OPS: </span>
                  <span className={`font-bold ${isUp ? "text-green-400" : "text-red-400"}`}>
                    {player.recent_ops?.toFixed(3)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-astros-muted">Season ERA: </span>
                  <span className="text-white font-semibold">{player.season_era?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-astros-muted">Last 3 ERA: </span>
                  <span className={`font-bold ${isUp ? "text-green-400" : "text-red-400"}`}>
                    {player.recent_era?.toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function TrendsTab() {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const response = await axios.get(`${API}/stats/trends`);
        setTrends(response.data);
      } catch (e) {
        console.error("Error fetching trends:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTrends();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!trends) {
    return <div className="text-center py-12 text-astros-muted">Unable to load trends data</div>;
  }

  return (
    <div data-testid="trends-tab">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-2">PLAYER TRENDS</h2>
      <p className="text-astros-muted text-sm mb-6">
        Last 7 games performance for hitters and last 3 appearances for pitchers, compared to season averages. Players doing well recently are <span className="text-green-400">trending up</span>, those struggling are <span className="text-red-400">trending down</span>.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Trending Up */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <h3 className="font-bebas text-xl text-green-400">TRENDING UP</h3>
          </div>

          <div className="mb-6">
            <h4 className="text-xs text-astros-muted uppercase tracking-wider mb-3 font-bold">Hitters (Higher Recent OPS)</h4>
            <div className="space-y-2">
              {trends.trending_up?.hitters?.map((player) => (
                <TrendCard key={player.id} player={player} type="hitter" direction="up" />
              ))}
              {(!trends.trending_up?.hitters?.length) && <p className="text-astros-muted text-sm">No data</p>}
            </div>
          </div>

          <div>
            <h4 className="text-xs text-astros-muted uppercase tracking-wider mb-3 font-bold">Pitchers (Lower Recent ERA)</h4>
            <div className="space-y-2">
              {trends.trending_up?.pitchers?.map((player) => (
                <TrendCard key={player.id} player={player} type="pitcher" direction="up" />
              ))}
              {(!trends.trending_up?.pitchers?.length) && <p className="text-astros-muted text-sm">No data</p>}
            </div>
          </div>
        </div>

        {/* Trending Down */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="w-5 h-5 text-red-400" />
            <h3 className="font-bebas text-xl text-red-400">TRENDING DOWN</h3>
          </div>

          <div className="mb-6">
            <h4 className="text-xs text-astros-muted uppercase tracking-wider mb-3 font-bold">Hitters (Lower Recent OPS)</h4>
            <div className="space-y-2">
              {trends.trending_down?.hitters?.map((player) => (
                <TrendCard key={player.id} player={player} type="hitter" direction="down" />
              ))}
              {(!trends.trending_down?.hitters?.length) && <p className="text-astros-muted text-sm">No data</p>}
            </div>
          </div>

          <div>
            <h4 className="text-xs text-astros-muted uppercase tracking-wider mb-3 font-bold">Pitchers (Higher Recent ERA)</h4>
            <div className="space-y-2">
              {trends.trending_down?.pitchers?.map((player) => (
                <TrendCard key={player.id} player={player} type="pitcher" direction="down" />
              ))}
              {(!trends.trending_down?.pitchers?.length) && <p className="text-astros-muted text-sm">No data</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
